// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.
//
// D-POT-2 (Agent E) — the bits every /pot/billing tab shares: the billing.html page-local colours
// (its <style> block redefines --success #4CAF50 / --warn #D4943A … on top of facelift.css), the
// `.badge-*` chips, the `.fg` form-field look, the ghost / danger / success small buttons and the
// "Not available yet in OwlRx" disabled control used wherever the old app had an endpoint OwlRx lacks.
import type { CSSProperties, ReactNode } from 'react'
import { POT, potBtnSm } from '../potTheme'

/** billing.html :root — teal here means the blue #0078D4 (spec §1). */
export const BILL = {
  teal: POT.blue, tealLt: POT.blueLt,
  success: '#4CAF50', successLt: '#EDF7ED',
  danger: POT.danger, dangerLt: '#FEF0F0',
  warn: '#D4943A', warnLt: '#FFF7ED',
  purple: '#6B3FA0', purpleLt: '#F0E6FF',
} as const

export const GAP_TIP = 'Not available yet in OwlRx'

export const money = (v: number | string | null | undefined): string => `$${(Number(v) || 0).toFixed(2)}`
export const perMile = (v: number | string | null | undefined): string => `$${(Number(v) || 0).toFixed(3)}/mi`

/** `.badge .badge-<kind>` — same palette as billing.html. */
export type BadgeKind = 'active' | 'inactive' | 'draft' | 'sent' | 'signed' | 'expired' | 'voided' | 'hha' | 'nurse_independent' | 'neutral'
const BADGE: Record<BadgeKind, [string, string]> = {
  active: [BILL.successLt, BILL.success], inactive: [BILL.dangerLt, BILL.danger], draft: [BILL.warnLt, BILL.warn],
  sent: [BILL.tealLt, BILL.teal], signed: [BILL.successLt, BILL.success], expired: [POT.bg, POT.text3], voided: [BILL.dangerLt, BILL.danger],
  hha: [BILL.purpleLt, BILL.purple], nurse_independent: [BILL.tealLt, BILL.teal], neutral: [POT.bg, POT.text3],
}
export function Badge({ kind, children, title }: { kind: BadgeKind; children: ReactNode; title?: string }) {
  const [bg, fg] = BADGE[kind]
  return <span title={title} style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: bg, color: fg, whiteSpace: 'nowrap' }}>{children}</span>
}
/** ASP agency / LOA / invoice status word → badge colour. OwlRx invoice states map onto the nearest. */
export function statusBadgeKind(status: string | null | undefined): BadgeKind {
  const s = (status ?? '').toLowerCase()
  if (s === 'active') return 'active'
  if (s === 'inactive') return 'inactive'
  if (s === 'signed' || s === 'paid' || s === 'approved') return 'signed'
  if (s === 'voided' || s === 'rejected') return 'voided'
  if (s === 'draft' || s === 'partial') return 'draft'
  if (s === 'sent' || s === 'submitted' || s === 'pending_approval') return 'sent'
  if (s === 'expired') return 'expired'
  return 'neutral'
}

// ── `.fg` fields (billing.html modals) ───────────────────────────────────────────────────────
export const fgLabel: CSSProperties = { display: 'block', fontSize: 11, fontWeight: 600, color: POT.text2, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.04em' }
export const fgInput: CSSProperties = { width: '100%', padding: '9px 12px', border: `1px solid ${POT.borderDk}`, borderRadius: POT.radius, fontSize: 13, fontFamily: POT.font, boxSizing: 'border-box', background: '#fff', color: POT.text }
export const fgInputDisabled: CSSProperties = { ...fgInput, background: POT.bg, color: POT.text3, cursor: 'not-allowed' }
export const hint: CSSProperties = { fontSize: 11, color: POT.text3, marginTop: 4, minHeight: 14 }
/** The kit SelectField / DateInput drawn like a `.fg` input. */
export const kitSelectSx = { '& .MuiOutlinedInput-root': { borderRadius: '4px', fontSize: 13, fontFamily: POT.font, background: '#fff' }, '& fieldset': { borderColor: POT.borderDk }, '& .MuiSelect-select': { padding: '9px 12px' } }
export const kitDateStyle: CSSProperties = { width: '100%', padding: '6px 10px', border: `1px solid ${POT.borderDk}`, borderRadius: POT.radius, fontSize: 13, fontFamily: POT.font, background: '#fff' }

export function Fg({ label, children, htmlFor, style }: { label: ReactNode; children: ReactNode; htmlFor?: string; style?: CSSProperties }) {
  return (
    <div style={{ marginBottom: 12, ...style }}>
      <label htmlFor={htmlFor} style={fgLabel}>{label}</label>
      {children}
    </div>
  )
}
export const grid = (cols: number): CSSProperties => ({ display: 'grid', gap: 12, gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` })
export const subhead: CSSProperties = { fontSize: 12, fontWeight: 600, color: POT.text2, margin: '12px 0 8px' }
export const helpText: CSSProperties = { fontSize: 12, color: POT.text3, marginBottom: 12 }

// ── buttons (`.btn`, `.btn-primary`, `.btn-sm` + the inline ghost / danger / success variants) ──
export const btn: CSSProperties = { padding: '8px 16px', border: 'none', borderRadius: POT.radius, fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: POT.font }
export const btnPrimary: CSSProperties = { ...btn, background: BILL.teal, color: '#fff' }
export const btnGhost: CSSProperties = { ...btn, background: POT.bg, border: `1px solid ${POT.borderDk}`, color: POT.text2 }
export const btnSm: CSSProperties = { ...potBtnSm, fontWeight: 500 }
export const btnPrimarySm: CSSProperties = { ...btnSm, background: BILL.teal, color: '#fff' }
export const btnGhostSm: CSSProperties = { ...btnSm, background: POT.bg, border: `1px solid ${POT.borderDk}`, color: POT.text2 }
export const btnDangerSm: CSSProperties = { ...btnSm, background: BILL.dangerLt, color: BILL.danger, border: '1px solid #F5BABA' }
export const btnSuccessSm: CSSProperties = { ...btnSm, background: BILL.successLt, color: BILL.success, border: '1px solid #C0DD97' }
export const btnPdfSm: CSSProperties = { ...btnSm, background: BILL.tealLt, color: BILL.teal, border: `1px solid ${BILL.teal}` }

/** A control the old app had but OwlRx cannot serve yet: rendered disabled with the GAP tooltip. The
 *  tooltip sits on a wrapper because a disabled <button> swallows pointer events in some browsers. */
export function GapButton({ children, style, tip = GAP_TIP }: { children: ReactNode; style?: CSSProperties; tip?: string }) {
  return (
    <span title={tip} style={{ display: 'inline-block', cursor: 'not-allowed' }}>
      <button type="button" disabled aria-disabled="true" aria-label={`${typeof children === 'string' ? children : 'Action'} — ${tip}`}
        style={{ ...btnGhostSm, ...style, opacity: .5, pointerEvents: 'none' }}>{children}</button>
    </span>
  )
}

/** The centred `.empty` block under each tab's table. */
export function Empty({ children }: { children: ReactNode }) {
  return <div role="status" style={{ textAlign: 'center', color: POT.text3, padding: 30, fontSize: 14 }}>{children}</div>
}

/** Patient column: initials (+ MRN on a second line), as the ASP visit row showed them. */
export function initialsOf(name: string | null | undefined): string {
  if (!name) return ''
  const parts = name.replace(/,/g, ' ').trim().split(/\s+/).filter(Boolean)
  return parts.map(p => p.charAt(0).toUpperCase()).join('')
}
